import {
  ChevronUp,
  ChevronDown,
  EyeIcon,
  UserCheck,
  Loader2,
} from 'lucide-react';
import { use, useEffect, useState } from 'react';
import { trpc } from '@/app/_trpc/client';
import {
  AccountType,
  MatchFeedback as PrismaMatchFeedback,
} from '@/autogenerated/@prisma/prisma-mongo-client';
import { AccountActionsDialog } from '@/components/features/admin/AccountActionsDialog';
import Avatar from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

import { MatchStatus } from '@/constants/matchStatus';
import { cn } from '@/lib/utils';
import AdminFeebackDialog, { FeedbackButton } from './AdminFeebackDialog';
import { DialogContext } from '../dialogs/infra/context/DialogContext';

export type MatchFeedback = Pick<
  PrismaMatchFeedback,
  'id' | 'explanation' | 'positiveExperience' | 'weMet' | 'createdDate'
> & { userId?: string };
interface TransformedMatch {
  id: string;
  matchStatus: MatchStatus;
  waitingForRefugee: boolean;
  withdrawnBy: string | undefined;
  user: {
    id: string;
    name: string;
    email: string;
    photo: string;
    accountType: AccountType;
    lastLoginDate: string | null;
  };
  matchFeedbacks: MatchFeedback[];
}

export default function AdminUserMatches({
  userId,
  accountType,
  userName,
}: {
  userId: string;
  accountType: AccountType;
  userName: string;
}) {
  const { data: rawMatches, isLoading } =
    trpc.admin.matches.getByUserId.useQuery({
      userId,
      accountType,
    });
  const [isExpanded, setIsExpanded] = useState(false);
  const [matches, setMatches] = useState<TransformedMatch[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<MatchFeedback[]>([]);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { openDialog } = use(DialogContext);

  useEffect(() => {
    if (rawMatches) {
      if (rawMatches.length <= 1) {
        setIsExpanded(true);
      }
    }

    // Create a new array to avoid type issues
    const transformedMatches: TransformedMatch[] = [];

    if (rawMatches) {
      for (const match of rawMatches) {
        const otherUser =
          accountType === AccountType.Refugee
            ? match.localUser
            : match.refugeeUser;

        if (!otherUser) {
          continue;
        }
        transformedMatches.push({
          id: match.id,
          matchStatus: match.matchStatus as MatchStatus,
          withdrawnBy: match.withdrawnBy,
          waitingForRefugee:
            match.matchStatus === MatchStatus.Pending &&
            !!match.createdBySupporter,
          user: {
            id: otherUser.id,
            name: otherUser.name ?? '',
            email: otherUser.email ?? '',
            photo: otherUser.profile.profilePhoto ?? '',
            accountType: otherUser.accountType ?? AccountType.Refugee,
            lastLoginDate: otherUser.lastLoginDate ?? null,
          },
          matchFeedbacks: match.matchFeedbacks?.map((feedback) => ({
            id: feedback.id,
            explanation: feedback.explanation,
            positiveExperience: feedback.positiveExperience,
            weMet: feedback.weMet,
            createdDate: feedback.createdDate,
            userId: feedback.userId,
          })),
        });
      }
    }

    setMatches(transformedMatches);
  }, [isLoading, rawMatches, accountType]);

  const handleShowFeedback = (match: (typeof matches)[0]) => {
    setSelectedFeedback(match.matchFeedbacks);
    setFeedbackOpen(true);
  };

  const renderMatches = () => {
    if (!matches) {
      return <div>No matches found</div>;
    }
    return (
      <>
        <div className='relative'>
          <div
            className={cn(
              'grid gap-4 overflow-hidden transition-all duration-500 ease-in-out',
              {
                'max-h-[250px]': !isExpanded,
                'max-h-[2000px] overflow-y-auto': isExpanded,
              }
            )}
          >
            {matches.map((match) => (
              <Card key={match.id} className='overflow-hidden'>
                <CardContent className='p-0'>
                  <div className='flex items-center p-4'>
                    <Avatar
                      className='mr-4 h-12 w-12'
                      imageUrl={match.user.photo}
                      name={match.user.name}
                    />

                    <div className='flex-1'>
                      <h3 className='font-medium'>{match.user.name}</h3>

                      <Badge
                        className={cn('px-3 py-1 text-white', {
                          'bg-green-600':
                            match.matchStatus === MatchStatus.Matched,
                          'bg-yellow-500':
                            match.matchStatus === MatchStatus.Pending,
                          'bg-red-500':
                            match.matchStatus === MatchStatus.AdminDeclined ||
                            match.matchStatus === MatchStatus.Declined,
                          'bg-primary-500':
                            match.matchStatus === MatchStatus.Closed ||
                            match.matchStatus === MatchStatus.AwaitingFeedback,
                          'bg-purple-500':
                            match.matchStatus === MatchStatus.ForReview,
                          'bg-gray-500':
                            match.matchStatus === MatchStatus.Withdrawn,
                        })}
                      >
                        <UserCheck className='mr-1 h-3 w-3' />
                        {match.matchStatus}
                        {match.waitingForRefugee && <> [Waiting for refugee]</>}
                        {match.matchStatus === MatchStatus.Withdrawn &&
                          match.withdrawnBy !== match.user.id && (
                            <> [by {userName}]</>
                          )}
                      </Badge>
                    </div>

                    <div className='flex items-center gap-2'>
                      <FeedbackButton
                        matchCount={match.matchFeedbacks.length}
                        onClick={() => handleShowFeedback(match)}
                      />
                      <Button
                        variant='ghost'
                        size='icon'
                        title='View Profile'
                        onClick={() =>
                          openDialog('profile', { userId: match.user.id })
                        }
                      >
                        <EyeIcon className='h-4 w-4' />
                      </Button>

                      <AccountActionsDialog user={match.user} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          {/* Gradient overlay for collapsed state */}
          {!isExpanded && matches.length > 1 && (
            <div className='from-background pointer-events-none absolute right-0 bottom-0 left-0 h-24 bg-gradient-to-t to-transparent' />
          )}
        </div>
        {/* Toggle button */}
        {matches.length > 1 && (
          <div className='mt-4 flex justify-center'>
            <Button
              variant='outline'
              onClick={() => setIsExpanded(!isExpanded)}
              className='flex items-center gap-2'
            >
              {isExpanded ? (
                <>
                  <ChevronUp className='h-4 w-4' />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className='h-4 w-4' />
                  Show all {matches.length} matches
                </>
              )}
            </Button>
          </div>
        )}
        <AdminFeebackDialog
          feedbackOpen={feedbackOpen}
          setFeedbackOpen={setFeedbackOpen}
          selectedFeedback={selectedFeedback}
          currentUserId={userId}
          otherUserId={matches[0]?.user.id}
          currentUserName={userName}
          otherUserName={matches[0]?.user.name}
        />
      </>
    );
  };

  return (
    <div className='container mx-auto py-6'>
      <div className='mb-6 flex items-center justify-between'>
        <h1 className='text-2xl font-bold'>{userName}&apos;s Matches</h1>
        {matches && (
          <span className='text-muted-foreground text-sm'>
            {matches.length} matches
          </span>
        )}
      </div>
      {isLoading ? (
        <div className='flex items-center justify-center'>
          <Loader2 className='h-4 w-4 animate-spin' />
        </div>
      ) : (
        renderMatches()
      )}
    </div>
  );
}
